import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseConfig, FuseConfigService, Scheme } from '@fuse/services/config';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { MODULE_PERMISSION_KEYS, PermissionService } from 'app/core/permission/permission.service';
import { PwaUpdateService } from 'app/core/pwa/pwa-update.service';
import { writeStoredScheme } from 'app/core/theme/scheme-preference';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { APP_VERSION } from 'app/core/version/app-version';
import { AppInstallButtonComponent } from 'app/layout/common/app-install/app-install-button.component';
import { CompanySwitcherComponent } from 'app/layout/common/company-switcher/company-switcher.component';
import { PrimaryColorPickerComponent } from 'app/layout/common/primary-color-picker/primary-color-picker.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'sidebar-account',
    templateUrl: './sidebar-account.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
        CompanySwitcherComponent,
        PrimaryColorPickerComponent,
        AppInstallButtonComponent,
        UserComponent,
    ],
    styles: [`
        :host {
            display: block;
            width: 100%;
            padding-bottom: 6px;
        }

        .sidebar-account-trigger {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            margin: 0;
            padding: 10px 12px;
            border: 0;
            border-top: 1px solid var(--fuse-border, rgba(15, 23, 42, 0.08));
            background: transparent;
            text-align: left;
            cursor: pointer;
            transition: background-color 0.15s ease;
        }

        .sidebar-account-trigger:hover {
            background: rgba(15, 23, 42, 0.04);
        }

        .sidebar-account-trigger:focus-visible {
            outline: 2px solid rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.55);
            outline-offset: -2px;
        }

        .sidebar-account-avatar {
            display: flex;
            flex: 0 0 auto;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            overflow: hidden;
            border-radius: 9999px;
            background: var(--fuse-primary, #017e84);
            box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
        }

        .sidebar-account-avatar-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .sidebar-account-avatar-initials {
            font-size: 13px;
            font-weight: 700;
            line-height: 1;
            letter-spacing: 0.02em;
            color: var(--fuse-on-primary, #fff);
        }

        .sidebar-account-meta {
            display: flex;
            flex: 1 1 auto;
            flex-direction: column;
            min-width: 0;
            line-height: 1.2;
        }

        .sidebar-account-name,
        .sidebar-account-subtitle {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .sidebar-account-name {
            font-size: 13px;
            font-weight: 600;
            color: var(--fuse-text-default, #1e293b);
        }

        .sidebar-account-subtitle {
            margin-top: 2px;
            font-size: 11px;
            font-weight: 500;
            color: var(--fuse-text-secondary, #64748b);
        }

        .sidebar-account-more {
            flex: 0 0 auto;
            color: var(--fuse-text-secondary, #64748b);
        }

        .sidebar-account-menu.mat-mdc-menu-panel {
            min-width: 256px !important;
            max-width: 280px !important;
            overflow: hidden;
        }

        .sidebar-account-menu .mat-mdc-menu-content {
            padding: 0 !important;
            max-height: min(480px, calc(100vh - 80px));
            overflow-y: auto;
        }

        .sidebar-account-panel {
            display: flex;
            flex-direction: column;
            padding: 6px;
        }

        .sidebar-account-panel app-install-button,
        .sidebar-account-panel user,
        .user-embedded {
            display: block;
            width: 100%;
        }

        .sidebar-account-action {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            min-height: 36px;
            padding: 0 8px;
            border: 0;
            border-radius: 8px;
            background: transparent;
            color: inherit;
            font: inherit;
            font-size: 13px;
            font-weight: 500;
            text-align: left;
            cursor: pointer;
        }

        .sidebar-account-action:hover {
            background: rgba(15, 23, 42, 0.06);
        }

        .sidebar-account-action-icon {
            color: var(--fuse-text-secondary, #64748b);
        }

        .sidebar-account-divider {
            height: 1px;
            margin: 6px 4px;
            background: var(--fuse-border, rgba(15, 23, 42, 0.08));
        }

        .sidebar-account-version {
            padding: 0 12px 4px;
            font-size: 10px;
            font-weight: 500;
            line-height: 1.3;
            text-align: center;
            color: var(--fuse-text-secondary, #64748b);
            opacity: 0.8;
        }

        .dark .sidebar-account-trigger:hover {
            background: rgba(248, 250, 252, 0.06);
        }

        .dark .sidebar-account-name {
            color: #f1f5f9;
        }

        .dark .sidebar-account-subtitle,
        .dark .sidebar-account-more,
        .dark .sidebar-account-version,
        .dark .sidebar-account-action-icon {
            color: #94a3b8;
        }

        .dark .sidebar-account-action:hover {
            background: rgba(248, 250, 252, 0.08);
        }

        .dark .sidebar-account-divider {
            background: rgba(248, 250, 252, 0.12);
        }
    `],
})
export class SidebarAccountComponent implements OnInit, OnDestroy {
    @ViewChild(MatMenuTrigger) private _accountMenuTrigger?: MatMenuTrigger;

    readonly appVersion = APP_VERSION;
    user: User | null = null;
    isDark = false;
    canAccessPos = false;

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private _authService: AuthService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseConfigService: FuseConfigService,
        private _permissionService: PermissionService,
        private _pwaUpdateService: PwaUpdateService,
        private _router: Router,
        private _userService: UserService
    ) {}

    get displayName(): string {
        const user = this.user;
        if (!user) {
            return 'Account';
        }

        const fromParts = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
        return user.fullName || user.name || fromParts || user.email || 'Account';
    }

    get initials(): string {
        const parts = this.displayName.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        }
        return (parts[0]?.[0] || 'A').toUpperCase();
    }

    get avatarUrl(): string | null {
        return this.user?.profilePicture || this.user?.avatar || null;
    }

    get subtitle(): string {
        return (
            this._authService.getCurrentCompanyName() ||
            this.user?.email ||
            'Workspace'
        );
    }

    ngOnInit(): void {
        this.canAccessPos = this._permissionService.hasPermission(
            MODULE_PERMISSION_KEYS.pos
        );

        this._userService.user$
            .pipe(takeUntil(this._destroy$))
            .subscribe((user) => {
                this.user = user;
                this.canAccessPos = this._permissionService.hasPermission(
                    MODULE_PERMISSION_KEYS.pos
                );
                this._changeDetectorRef.markForCheck();
            });

        this._fuseConfigService.config$
            .pipe(takeUntil(this._destroy$))
            .subscribe((config: FuseConfig) => {
                this.isDark = config.scheme === 'dark';
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    toggleScheme(): void {
        const nextScheme: Scheme = this.isDark ? 'light' : 'dark';

        // Close the menu first — toggling theme while it is open can leave a
        // stale CDK overlay that blocks the whole app until refresh.
        this._accountMenuTrigger?.closeMenu();
        writeStoredScheme(nextScheme);

        requestAnimationFrame(() => {
            this._fuseConfigService.config = { scheme: nextScheme };
        });
    }

    hardRefresh(): void {
        void this._pwaUpdateService.hardRefresh();
    }

    openPos(): void {
        const link =
            this._permissionService.findFirstPermittedLinkInModule('pos') ??
            '/pos/dashboard';
        this._router.navigateByUrl(link);
    }
}
